// The persistent global chat surface (N-CHAT-1), recreated 1:1 from the prototype (app.js renderChat +
// app.css .chat/.chat-launch/.chat-head/.chat-mid/.chat-hist). It is reachable on EVERY screen without a
// route change — a floating dock over the current surface, not a tab you navigate to. Three states (design
// brief §4):
//   • closed  → only the floating launcher shows (bottom-right; the App renders it as the .app sibling)
//   • compact → the 400×620 floating panel; the App's .chat-scrim provides the faint click-off wash
//   • full    → the panel expands beside the sidebar (the .app.chat-full geometry); conversation ~800px
// The history view flips the compact panel / overlays a rail in full (prototype .chat.hist-on); Escape steps
// down one level via the shell ladder.
//
// THIN TRANSPORT (N-CHAT-4): the dashboard reasons about nothing, assembles no Context, and is NOT
// screen-aware — this frame holds NO reference to the active surface, by design. The conversation is driven
// by the `useChat` controller: a send routes to agent.spawnAgentRun (carrying ONLY the human's message — no
// screen id), the transcript renders EXACTLY getRunTranscript, and a human can interrupt a running run
// (the agent cannot). The dock authors no turn (P-SELF).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import { useShell } from './ShellContext.tsx';
import type { ShellState } from './shell-state.ts';
import { useChat } from '../surfaces/useChat.ts';
import type { ChatController } from '../surfaces/useChat.ts';
import { ChatConversation } from './ChatConversation.tsx';
import { ChatHistory } from './ChatHistory.tsx';

/** The floating launcher shown when chat is closed (prototype .chat-launch — the App mounts it). */
export function ChatLauncher({ onOpen }: { readonly onOpen: () => void }): React.JSX.Element {
  return (
    <button type="button" onClick={onOpen} className="chat-launch">
      <span className="dot" aria-hidden="true" /> Ask Ritona
    </button>
  );
}

/** A square icon-button in the chat header (prototype .icon-btn: history / expand / close). */
function HeaderButton({
  label,
  icon,
  size = 15,
  pressed,
  onClick,
}: {
  readonly label: string;
  readonly icon: IconName;
  readonly size?: number;
  readonly pressed?: boolean;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className="icon-btn"
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

/** The chat header (prototype .chat-head): the on-the-record identity + history/expand/close controls. */
function ChatHeader({ full }: { readonly full: boolean }): React.JSX.Element {
  const shell = useShell();
  return (
    <div className="chat-head">
      <div>
        <div className="t">
          <span className="dot" aria-hidden="true" /> Ritona
        </div>
        <div className="note">on the record · every action logged</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <HeaderButton
          label="Conversation history"
          icon="history"
          pressed={shell.historyOpen}
          onClick={shell.toggleHistory}
        />
        <HeaderButton
          label={full ? 'Collapse chat' : 'Expand chat'}
          icon={full ? 'shrink' : 'expand'}
          onClick={() => shell.setChatFull(!full)}
        />
        <HeaderButton label="Close chat" icon="x" size={16} onClick={shell.closeChat} />
      </div>
    </div>
  );
}

/** Consume a surface's fresh-chat command only after the controller has invalidated the old context. */
function useFreshChatSwitch(chat: ChatController, shell: ShellState, onSwitch: () => void): void {
  const handledRequest = useRef<number | null>(null);
  const request = shell.pendingFreshChat;
  useLayoutEffect(() => {
    if (request === null || handledRequest.current === request.id) return;
    handledRequest.current = request.id;
    chat.newConversation();
    onSwitch();
    shell.applyFreshChat(request.id);
  }, [chat.newConversation, onSwitch, request, shell.applyFreshChat]);
}

/** Bind the active draft after a Session is minted/resumed, except during a staged fresh switch. */
function useSessionDraftBinding(chat: ChatController, shell: ShellState): void {
  const sessionId = chat.activeSessionId;
  const freshRequest = shell.pendingFreshChat;
  useEffect(() => {
    if (freshRequest !== null || sessionId === null) return;
    shell.bindChatDraftSession(sessionId);
  }, [freshRequest, sessionId, shell.bindChatDraftSession]);
}

/** History/new-chat callbacks with the same controller-first draft-switch ordering. */
function useConversationSwitches(
  chat: ChatController,
  shell: ShellState,
  onSwitch: () => void,
): {
  readonly openConversation: (sessionId: string) => void;
  readonly newConversation: () => void;
} {
  const openConversation = useCallback(
    (sessionId: string): void => {
      void chat.openConversation(sessionId);
      shell.openChatSessionDraft(sessionId);
      onSwitch();
      shell.closeHistory();
    },
    [chat.openConversation, onSwitch, shell.closeHistory, shell.openChatSessionDraft],
  );
  const newConversation = useCallback((): void => {
    chat.newConversation();
    shell.beginBlankChatDraft();
    onSwitch();
    shell.closeHistory();
  }, [chat.newConversation, onSwitch, shell.beginBlankChatDraft, shell.closeHistory]);
  return { openConversation, newConversation };
}

/** The OPEN chat (compact panel or full page — the `.app` classes drive the geometry). The `useChat`
 *  controller lives here so the conversation state survives the compact↔full toggle (one thread, N-CHAT).
 *  The history rail (BUILD-DEC-098) is the LIVE owner-scoped session list — see ChatHistory.tsx. */
function OpenChat({ full }: { readonly full: boolean }): React.JSX.Element {
  const shell = useShell();
  const chat: ChatController = useChat();
  // A conversation SWITCH re-keys the conversation region below, so it remounts. The composer's unsent
  // draft is the one piece of per-conversation state the controller does not own, and it must not follow
  // the human into the conversation they opened and be sent there — `useChat` already aborts an in-flight
  // SEND across the same boundary (sendGenRef), and the human's UNSENT text needs the same discipline.
  // Counted rather than keyed by session id: that id is MINTED mid-send on a conversation's first message,
  // so keying by it would remount the composer under the human's fingers as their first message goes out.
  const [conversationKey, setConversationKey] = useState(0);
  const onSwitch = useCallback(() => setConversationKey((key) => key + 1), []);
  useFreshChatSwitch(chat, shell, onSwitch);
  useSessionDraftBinding(chat, shell);
  const switches = useConversationSwitches(chat, shell, onSwitch);

  return (
    <aside
      role="dialog"
      aria-label="Ask Ritona"
      className={shell.historyOpen ? 'chat hist-on' : 'chat'}
    >
      <ChatHeader full={full} />
      <div className="chat-mid">
        {shell.historyOpen ? (
          <ChatHistory
            chat={chat}
            onOpenConversation={switches.openConversation}
            onNewConversation={switches.newConversation}
          />
        ) : null}
        <div className="chat-main">
          <ChatConversation key={conversationKey} chat={chat} />
        </div>
      </div>
    </aside>
  );
}

/** The chat dock: nothing when closed (the App renders the launcher); the open panel otherwise. */
export function ChatDock(): React.JSX.Element | null {
  const shell = useShell();
  if (shell.chat === 'closed') return null;
  return <OpenChat full={shell.chat === 'full'} />;
}
